/**
 * Create company hook
 * Handles the business logic for creating a company with administrator account via Cloud Function
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { COMPANY_DEFAULTS } from '@shared/constants/app';
import { getErrorMessage } from '@shared/utils/error';
import { generatePassword } from '@shared/utils/password';

export interface CreateCompanyFormData {
  companyName: string;
  companyEmail: string;
  companyPhone?: string;
  companyAddress?: string;
  adminFirstName: string;
  adminLastName: string;
  adminPhone?: string;
}

export interface CreateCompanyResult {
  success: boolean;
  password?: string;
  adminEmail?: string;
  adminFirstName?: string;
}

export const useCreateCompany = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const createCompanyWithAdmin = async (
    formData: CreateCompanyFormData
  ): Promise<CreateCompanyResult> => {
    setLoading(true);
    setError('');

    try {
      // Generate password
      const password = generatePassword();

      // Call Cloud Function to create company and administrator account
      const createCompanyAccount = httpsCallable(functions, 'createCompanyAccount');
      await createCompanyAccount({
        companyName: formData.companyName,
        companyEmail: formData.companyEmail,
        companyPhone: formData.companyPhone || '',
        companyAddress: formData.companyAddress || '',
        adminFirstName: formData.adminFirstName,
        adminLastName: formData.adminLastName,
        adminPhone: formData.adminPhone || '',
        adminPassword: password,
        maxUsers: COMPANY_DEFAULTS.MAX_USERS,
      });

      return {
        success: true,
        password,
        adminEmail: formData.companyEmail,
        adminFirstName: formData.adminFirstName,
      };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);

      return {
        success: false,
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    createCompanyWithAdmin,
    loading,
    error,
    resetLoading: () => setLoading(false),
  };
};
